import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Grid
} from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import { honorariosSolicitudDetalleJWT } from "../../api/endpoints";

// TODO: Migrate makeStyles to sx prop. Kept as-is because this component uses @global, @page,
// and @media print rules that cannot be expressed with inline sx props.
const useStyles = makeStyles((theme) => ({
    "@global": {
        "@page": {
            size: "portrait",
            margin: "10mm",
        },
    },
    root: {
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: 9999,
        padding: theme.spacing(4),
        backgroundColor: "#fff",
        color: "#000",
        overflow: "auto",
        boxSizing: "border-box",
        "@media print": {
            position: "static", // Allow flow for pagination
            width: "100%",
            height: "auto",
            overflow: "visible",
            padding: 0, // Let margins handle it
            margin: 0,
        },
    },
    header: {
        marginBottom: theme.spacing(4),
        textAlign: "center",
        borderBottom: "2px solid #000",
        paddingBottom: theme.spacing(2),
    },
    title: {
        fontWeight: "bold",
        textTransform: "uppercase",
        marginBottom: theme.spacing(1),
        "@media print": {
            fontSize: "1.5rem", // Reduce size for print
        },
    },
    tableContainer: {
        marginBottom: theme.spacing(4),
        overflow: "visible", // Critical for printing full table
    },
    table: {
        width: "100%",
        borderCollapse: "collapse",
        "& th, & td": {
            border: "1px solid #ccc",
            padding: "4px 8px", // Compact padding
            fontSize: "0.85rem", // Compact font
        },
        "@media print": {
            // Ensure header repeats on new pages if browser supports
            "& thead": { display: "table-header-group" },
            "& tbody": { display: "table-row-group" },
        },
    },
    pageBreak: {
        pageBreakInside: "avoid",
    },
    signatures: {
        marginTop: theme.spacing(8),
        marginBottom: theme.spacing(4),
        pageBreakInside: "avoid", // Keep signatures together
    },
    totalSection: {
        display: "flex",
        justifyContent: "flex-end",
        marginTop: theme.spacing(2),
        marginRight: theme.spacing(2),
    },
    totalText: {
        fontWeight: "bold",
        fontSize: "1.2rem",
    },
    signatureLine: {
        borderTop: "1px solid #000",
        width: "80%",
        margin: "0 auto",
        paddingTop: theme.spacing(1),
        textAlign: "center",
    },
}));

const SolicitudImpresion = () => {
    const classes = useStyles();
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [solicitud, setSolicitud] = useState(null);
    const [certificados, setCertificados] = useState([]);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchDetalle();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const fetchDetalle = async () => {
        try {
            const response = await honorariosSolicitudDetalleJWT(id);
            if (response.ok) {
                setSolicitud(response.solicitud);
                setCertificados(response.certificados);
                if (!window._sigifPrinted) {
                    window._sigifPrinted = true;
                    setTimeout(() => { window.print(); }, 800);
                }
            } else {
                setError(response.mensaje || "Error al cargar la solicitud");
            }
        } catch (err) {
            setError(err.message || "Error de conexión");
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={4}>
                <Typography color="error" variant="h6">Error: {error}</Typography>
            </Box>
        );
    }

    if (!solicitud) return null;

    return (
        <Box className={classes.root} id="printable-area">
            {/* Header */}
            <Box className={classes.header}>
                <Typography variant="h4" className={classes.title}>
                    Fiscalía de Estado
                </Typography>
                <Typography variant="h6" className={classes.subtitle}>
                    Solicitud de Pago de Honorarios - Planes de Pago
                </Typography>
            </Box>

            {/* Info Section */}
            <Grid container className={classes.infoSection}>
                <Grid size={6}>
                    <Box className={classes.infoRow}>
                        <Typography><strong>Solicitud Nº:</strong> {solicitud.Id}</Typography>
                    </Box>
                    <Box className={classes.infoRow}>
                        <Typography><strong>Fecha:</strong> {solicitud.Fecha}</Typography>
                    </Box>
                    <Box className={classes.infoRow}>
                        <Typography><strong>Estado:</strong> {solicitud.Estado}</Typography>
                    </Box>
                </Grid>
                <Grid size={6}>
                    <Box className={classes.infoRow}>
                        <Typography><strong>Profesional:</strong> {solicitud.Profesional}</Typography>
                    </Box>
                    <Box className={classes.infoRow}>
                        <Typography><strong>Matrícula:</strong> {solicitud.Matricula_Provincial}</Typography>
                    </Box>
                    <Box className={classes.infoRow}>
                        <Typography><strong>DNI/CUIT:</strong> {solicitud.Cuit_Abogado || "-"}</Typography>
                    </Box>
                </Grid>
            </Grid>

            {/* Certificates Table */}
            <TableContainer className={classes.tableContainer}>
                <Table className={classes.table} size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell className={classes.tableHeader}>Fecha Plan</TableCell>
                            <TableCell className={classes.tableHeader}>Autos</TableCell>
                            <TableCell className={classes.tableHeader}>Nro Plan</TableCell>
                            <TableCell className={classes.tableHeader}>Certif</TableCell>
                            <TableCell className={classes.tableHeader}>Demandado</TableCell>
                            <TableCell className={classes.tableHeader} align="right">Costas</TableCell>
                            <TableCell className={classes.tableHeader} align="right">IVA</TableCell>
                            <TableCell className={classes.tableHeader} align="right">Total</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {certificados.map((cert, idx) => (
                            <TableRow key={cert.Id} sx={{ backgroundColor: idx % 2 === 0 ? "rgba(0,0,0,0.02)" : "transparent" }}>
                                <TableCell>{cert.FPago}</TableCell>
                                <TableCell>{cert.Expediente_Autos}</TableCell>
                                <TableCell>{cert.Ref_Nro_Plan}</TableCell>
                                <TableCell>{cert.Letra}-{cert.Numero}</TableCell>
                                <TableCell>{cert.Demandado_NN}</TableCell>
                                <TableCell align="right">
                                    $ {(parseFloat(cert.Costas) || 0).toLocaleString("es-AR", { minimumFractionDigits: 2 })}
                                </TableCell>
                                <TableCell align="right">
                                    $ {(parseFloat(cert.Costas_IVA) || 0).toLocaleString("es-AR", { minimumFractionDigits: 2 })}
                                </TableCell>
                                <TableCell align="right">
                                    $ {((parseFloat(cert.Costas) || 0) + (parseFloat(cert.Costas_IVA) || 0)).toLocaleString("es-AR", { minimumFractionDigits: 2 })}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Total Section */}
            <Box className={classes.totalSection}>
                <Typography className={classes.totalText}>
                    Total a Pagar: $ {(parseFloat(solicitud.Honorarios) || 0).toLocaleString("es-AR", { minimumFractionDigits: 2 })}
                </Typography>
            </Box>

            {/* Signatures */}
            <Grid container className={classes.signatures}>
                <Grid size={6}>
                    <Box className={classes.signatureLine}>
                        <Typography variant="body2">Firma del Profesional</Typography>
                    </Box>
                </Grid>
                <Grid size={6}>
                    <Box className={classes.signatureLine}>
                        <Typography variant="body2">Recepción Fiscalía de Estado</Typography>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default SolicitudImpresion;
